import React, { useContext, useEffect, useState } from 'react';
import {
  BlogArticleFragment,
  BlogAuthorFragment,
  BlogTopicFragment,
} from '../../../../__generated__/gatsby-types';
import { Box, createStyles, Grid, Theme, Typography } from '@material-ui/core';
import PageTitle from '../../page-title';
import ArticleCard from '../../article-card';
import PgeButton from '../../../pge-button/_PgeButton';
import RelatedTopics from '../../related-topics/src/RelatedTopics';
import SocialFollowLinks from '../../social-follow-links';
import { useIsMobile } from '../../../../util/style-utils';
import { makeStyles } from '@material-ui/core/styles';
import {
  DEFAULT_LANGUAGE,
  LanguageContext,
} from '../../../../providers/LanguageProvider';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import { useBlogBlurb, BlogBlurb } from '../../blog-blurb'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rootSpacing: {
      width: '100%',
      margin: 'unset',
      padding: '40px 0',
    },
    gridPaddingRight: {
      [theme.breakpoints.up('sm')]: {
        paddingRight: '30px',
      },
    },
    gridPaddingLeft: {
      [theme.breakpoints.up('sm')]: {
        paddingLeft: '30px',
      },
    },
    itemWrapper: {
      marginBottom: '30px',
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        flexBasis: 'calc(50% - 15px)',
      },
      [theme.breakpoints.down('sm')]: {
        paddingRight: 0,
        paddingLeft: 0,
      },
    },
    descriptionText: {
      fontSize: theme.spacing(1.75),
      fontStyle: 'italic',
    },
    horizontalLine: {
      margin: '30px 0',
      color: colors.lightGray1,
      backgroundColor: colors.lightGray1,
      borderColor: colors.lightGray1,
      borderStyle: 'solid',
    },
    hr: {
      margin: '30px 0',
      color: colors.lightGray1,
      backgroundColor: colors.lightGray1,
      borderColor: colors.lightGray1,
      borderStyle: 'solid',
    },
  }),
);

type Props = {
  title: string;
  currentTopic?: any;
  data: any;
  topicHeader?: any;
};

const RelatedPosts = (props: Props) => {
  const { title, currentTopic, data, topicHeader } = props;
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { language } = useContext(LanguageContext);
  const { showBlogBlurb } = useBlogBlurb();

  const articlesToShowOnload = isMobile ? 5 : 6;

  const [isShowMoreButtonDisplayed, setIsShowMoreButtonDisplayed] = useState(
    false,
  );
  const [nextArticle, setNextArticle] = useState<number>(articlesToShowOnload);
  const [articles, setArticles] = useState<BlogArticleFragment[]>([]);
  const [shownArticles, setShownArticles] = useState<BlogArticleFragment[]>([]);
  const [shownTopics, setShownTopics] = useState<BlogTopicFragment[]>([]);

  useEffect(() => {
    const articleData: BlogArticleFragment[] = data?.articleData?.nodes.filter(
      // (node: BlogAuthorFragment) => node.node_locale === language,
      (node: BlogAuthorFragment) => node.node_locale === DEFAULT_LANGUAGE, // Temporarily disable localization on public pages.
    );
    const topics = articleData
      ?.map((article: any) => article.topics)
      .reduce((acc: BlogTopicFragment[], topic: BlogTopicFragment | null) => {
        if (topic !== null) {
          acc.push(topic);
        }
        return acc;
      }, [])
      .flat();
    setShownTopics(topics);
    setArticles(articleData);
    const articlesToShow = articleData.slice(0, articlesToShowOnload);
    setShownArticles(articlesToShow);
    setIsShowMoreButtonDisplayed(articlesToShowOnload < articleData.length);
  }, []);

  const handleLoadMoreArticles = () => {
    const newArticleIndex = nextArticle + (isMobile ? 5 : 6);
    const moreArticles = articles.slice(nextArticle, newArticleIndex);
    const moreArticlesUpdate = shownArticles.concat(moreArticles);
    setShownArticles(moreArticlesUpdate);
    setNextArticle(newArticleIndex);
    setIsShowMoreButtonDisplayed(newArticleIndex < articles.length);
  };

  return (
    <>
      <Grid container className={classes.rootSpacing}>
        <Grid item xs={12} sm={8} className={classes.gridPaddingRight}>
          <PageTitle
            title={title}
            subHeading
            style={{
              marginTop: isMobile ? 20 : 30,
              padding: isMobile ? '0 20px' : '0',
            }}
          />
          {shownArticles
            .filter((node: any) => node.node_locale === language)
            .map((item: BlogArticleFragment) => (
              <Grid
                item
                sm={12}
                md={12}
                className={classes.itemWrapper}
                key={item.id}
              >
                <ArticleCard item={item} style={{ maxWidth: '100%' }} />
              </Grid>
            ))}
          <hr className={classes.hr} />
          <Box
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            mb={isMobile ? 3.75 : 0}
          >
            {isShowMoreButtonDisplayed ? (
              <PgeButton theme="Secondary" onClick={handleLoadMoreArticles}>
                {t('SHOW_MORE')}
              </PgeButton>
            ) : null}
          </Box>
        </Grid>
        <Grid item xs={12} sm={4} className={classes.gridPaddingLeft}>
          <>
            {shownTopics && (
              <>
                <RelatedTopics
                  topics={shownTopics}
                  currentTopicId={currentTopic?.contentful_id || ''}
                  title={topicHeader}
                />
                <hr className={classes.horizontalLine} />
              </>
            )}
            {showBlogBlurb && (
              <>
                <BlogBlurb cssClass={classes.descriptionText} />
                <hr className={classes.horizontalLine} />
              </> 
            )}
            <SocialFollowLinks />
          </>
        </Grid>
      </Grid>
    </>
  );
};

export default RelatedPosts;
